import React, { useCallback, useState } from "react";
import { LoginPageProps } from "./SessionProvider";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { BaseLoginRequest, loginViaLdap, registerByLdap } from "../../api/Login";
import { InputFormField } from "../../common/formfields/InputFormField";
import { PasswordFormField } from "../../common/formfields/PasswordFormField";
import { LucideIcon } from "../../common/icon/LucideIcon";
import { Button } from "../../common/buttons/Button/Button";
import { ServerError } from "../../common/helperfunctions/ApiError";
import { Alert as AlertCustom } from "../../common/overlays/Alert/Alert";
import { Alert } from "react-bootstrap";
import { CheckboxFormField } from "../../common/formfields/CheckboxFormField/CheckboxFormField";

export interface LoginRequestForm extends BaseLoginRequest {
  rememberMe: boolean;
}

const initialValues: LoginRequestForm = {
  username: "",
  password: "",
  rememberMe: true,
} as const;

const validationSchema = yup.object().shape({
  username: yup.string().required("Email or username is required"),
  password: yup.string().required("Password is required"),
});

export const LdapLoginForm = ({ group, setSessionToken }: LoginPageProps) => {
  const [loginError, setLoginError] = useState<string | null>(null);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [hasToRegister, setHasToRegister] = useState<boolean>(false);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<LoginRequestForm>({ defaultValues: initialValues, resolver: yupResolver(validationSchema) });

  const handleLogin = useCallback(
    async ({ username, password, rememberMe }: LoginRequestForm) => {
      setLoginError(null);
      const loginResponse = await loginViaLdap(group, { username, password }).catch((e: ServerError) => {
        setLoginError(e.message);
        return;
      });
      if (loginResponse === undefined) return;

      if (loginResponse === null) {
        setLoginError("Invalid username or password");
        return;
      }
      if (!loginResponse.success) {
        setLoginError("Invalid username or password");
        return;
      }

      if (!loginResponse.hasUser) {
        setHasToRegister(true);
        return;
      }

      if (loginResponse.session.sessionId) {
        if (rememberMe) {
          localStorage.setItem(`session_${group}`, loginResponse.session.sessionId as string);
        } else {
          sessionStorage.setItem(`session_${group}`, loginResponse.session.sessionId as string);
        }
        setSessionToken(loginResponse.session.sessionId);
      }
    },
    [group, setSessionToken]
  );

  const registerUser = useCallback(
    async ({ username, password, rememberMe }: LoginRequestForm) => {
      const response = await registerByLdap(group, { username, password }).catch((e: ServerError) => {
        setRegisterError(e.message);
        return;
      });

      if (!!response && !!response.session.sessionId) {
        if (rememberMe) {
          localStorage.setItem(`session_${group}`, response.session.sessionId as string);
        } else {
          sessionStorage.setItem(`session_${group}`, response.session.sessionId as string);
        }
        setSessionToken(response.session.sessionId);
      }
    },
    [group, setSessionToken]
  );

  const currentUsername = watch("username");
  const currentPassword = watch("password");
  const currentRememberMe = watch("rememberMe");
  return (
    <form autoComplete="off" onSubmit={hasToRegister ? undefined : handleSubmit(handleLogin)}>
      <InputFormField
        id={"username"}
        label={"Username"}
        errors={errors}
        register={register}
        autoFocus={true}
        horizontal
        placeholder={"LDAP username"}
        disabled={hasToRegister}
        readOnly={hasToRegister}
      />

      <PasswordFormField
        id={"password"}
        label={"Password"}
        errors={errors}
        register={register}
        horizontal
        placeholder={"Password"}
        disabled={hasToRegister}
        readOnly={hasToRegister}
      />

      <CheckboxFormField id={"rememberMe"} label={""} control={control} checkboxStyle="square" uncontained>
        <div className="flex row-nowrap align-center" style={{ width: "100%", paddingLeft: 5 }}>
          <label className="control-label" style={{ marginBottom: 0 }}>
            Keep me logged in
          </label>
        </div>
      </CheckboxFormField>

      {loginError && <Alert bsStyle="danger">{loginError}</Alert>}

      {hasToRegister ? (
        <>
          <hr />
          <div style={{ fontWeight: "bold", fontSize: "1.2em", marginBottom: "1em" }}>Welcome {currentUsername}</div>
          <AlertCustom
            type="info"
            message={`It seems you haven't registered yet. Please click on "Register" to complete the registration process.`}
            fit
          />
          {registerError && <Alert bsStyle="danger">{registerError}</Alert>}
          <hr />
          <button
            className="btn btn-block btn-success"
            title="Register"
            type="button"
            onClick={async () =>
              await registerUser({
                username: currentUsername,
                password: currentPassword,
                rememberMe: currentRememberMe,
              })
            }
          >
            <LucideIcon name="user-round-pen" /> Register and log in
          </button>
          <button
            className="btn btn-block btn-soft-danger"
            title="Log out"
            onClick={() => {
              setHasToRegister(false);
              setRegisterError(null);
              reset();
            }}
          >
            <LucideIcon name="log-out" /> Cancel
          </button>
        </>
      ) : (
        <Button
          className="btn btn-primary btn-block"
          type="submit"
          loading={isSubmitting}
          style={{ marginLeft: "auto" }}
        >
          <LucideIcon name="log-in" /> Log in with Single Sign-On (LDAP)
        </Button>
      )}
    </form>
  );
};
